import React, { memo, useState, useRef, useMemo } from 'react';
import { useTableContext } from '../../context/TableContext';
import ConditionsDropdown from '../../../shared/ConditionsDropdown';
import { getEffectiveConditionsV2, getAllConditionTaskIds } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Dependency/Conditions cell for table view
 * 
 * Shows:
 * - If single block with few tasks: list of task names
 * - If multiple blocks or complex conditions: "Multiple conditions"
 * 
 * Opens the ConditionsDropdown modal on click.
 * Memoized to prevent re-renders when other cells change.
 */
const DependencyCell = memo(({ row }) => {
  const { allTasks, onConditionsChange } = useTableContext();
  const task = row.original;

  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  const v2 = useMemo(() => getEffectiveConditionsV2(task), [task]);
  const allTaskIds = useMemo(() => getAllConditionTaskIds(v2), [v2]);
  const taskMap = useMemo(() => new Map(allTasks.map(t => [t.id, t])), [allTasks]);

  const blockCount = v2.blocks?.length || 0;
  const hasMultipleBlocks = blockCount > 1;
  const totalTaskCount = allTaskIds.length;

  const handleButtonClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const renderSingleBlock = () => {
    if (blockCount === 0) return null;
    return allTaskIds.map((taskId) => {
      const depTask = taskMap.get(taskId);
      if (!depTask) return null;
      return (
        <span key={taskId} className="table-view__dep-tag">
          {depTask.name}
        </span>
      );
    });
  };

  return (
    <>
      <button
        ref={buttonRef}
        className="table-view__dependency-btn"
        onClick={handleButtonClick}
      >
        {totalTaskCount > 0 ? (
          <span className="table-view__dep-list">
            {hasMultipleBlocks ? (
              <span className="table-view__dep-multiple">
                Multiple conditions
              </span>
            ) : (
              renderSingleBlock()
            )}
          </span>
        ) : (
          <span className="table-view__dep-empty">—</span>
        )}
      </button>

      <ConditionsDropdown
        task={task}
        allTasks={allTasks}
        onConditionsChange={onConditionsChange}
        isOpen={isOpen}
        onClose={handleClose}
      />
    </>
  );
}, (prevProps, nextProps) => {
  const prevTask = prevProps.row.original;
  const nextTask = nextProps.row.original;
  return prevTask.id === nextTask.id &&
         prevTask.dependsOn === nextTask.dependsOn &&
         prevTask.unlockConditions === nextTask.unlockConditions;
});

export default DependencyCell;
